import { getUserColumns } from "../components/columns";
import { CreateUserDialog } from "../components/CreateUserDialog";
import { TableSettingsDialog } from "../components/TableSettingsDialog";
import { UsersStats } from "../components/UsersStats";
import { useUsersTableData } from "../hooks/useUsersTableData";
import {
    USER_ROLE_NAMES,
    USER_STATUS_NAMES,
} from "../types";
import { useUsersQuery } from "@/common/api/services/users";
import { DataTablePagination } from "@/components/shared/DataTable/DataTablePagination";
import { DataTableViewOptions } from "@/components/shared/DataTable/DataTableViewOptions";
import { PageHeader } from "@/components/shared/PageHeader";
import { SearchHeader } from "@/components/shared/SearchHeader";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import {
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { CameraOff, ChevronsDown, ChevronsUp, Plus, Users } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";

const UsersPage = () => {
    const { t } = useTranslation();
    const columns = getUserColumns(t);
    const [isContainerMode, setIsContainerMode] = React.useState(false);

    const {
        page,
        setPage,
        size,
        setSize,
        sorting,
        setSorting,
        columnFilters,
        setColumnFilters,
        columnVisibility,
        setColumnVisibility,
        searchQuery,
        setSearchQuery,
        roleFilter,
        statusFilter,
        handleRoleChange,
        handleStatusChange,
        sortField,
        sortOrder,
    } = useUsersTableData();

    const usersQuery = useUsersQuery();

    const {
        data: response,
        isLoading,
        error: usersError,
    } = useQuery(
        usersQuery.allUsersQuery({
            page,
            size,
            keyword: searchQuery || undefined,
            role: roleFilter !== "ALL" ? roleFilter : undefined,
            status: statusFilter !== "ALL" ? statusFilter : undefined,
            sortField,
            sortOrder,
        })
    );

    const users = response?.content || [];

    if (usersError) {
        return (
            <div className="container space-y-8 p-8">
                <PageHeader
                    icon={<Users />}
                    title={t("users.title")}
                    description={t("users.description")}
                />
                <div className="flex items-center justify-center h-32">
                    <div className="text-destructive">
                        {t("users.errors.loadingError")}
                    </div>
                </div>
            </div>
        );
    }

    const totalElements = response?.totalElements || 0;
    const sizeFromServer = response?.size || size;
    const totalPages = Math.ceil(totalElements / sizeFromServer);


    const table = useReactTable({
        data: users,
        columns,
        pageCount: totalPages,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            pagination: {
                pageIndex: page,
                pageSize: size,
            },
        },
        enableSorting: true,
        enableColumnFilters: true,
        enableRowSelection: false,
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onColumnVisibilityChange: setColumnVisibility,
        onPaginationChange: (updater) => {
            const newPagination =
                typeof updater === "function"
                    ? updater(table.getState().pagination)
                    : updater;

            if (newPagination.pageSize !== size) {
                setSize(newPagination.pageSize);
                setPage(0);
                return;
            }

            setPage(newPagination.pageIndex);
        },
        getCoreRowModel: getCoreRowModel(),
    });


    return (
        <div className={cn("space-y-8 p-8", isContainerMode && "container mx-auto")}>
            <div className="flex items-center justify-between">
                <PageHeader
                    icon={<Users />}
                    title={t("users.title")}
                    description={t("users.description")}
                />
                <div className="flex items-center gap-4">
                    <TableSettingsDialog
                        isContainerMode={isContainerMode}
                        onContainerModeChange={setIsContainerMode}
                    />
                    <CreateUserDialog>
                        <Button>
                            <Plus className="mr-2 h-4 w-4" />
                            {t("users.actions.addUser")}
                        </Button>
                    </CreateUserDialog>
                </div>
            </div>

            <UsersStats />

            <div className="space-y-4">
                <SearchHeader
                    searchPlaceholder={t("users.filters.search")}
                    value={searchQuery}
                    onSearch={setSearchQuery}
                    filters={
                        <>
                            <Select value={roleFilter} onValueChange={handleRoleChange}>
                                <SelectTrigger className="w-[180px]">
                                    <SelectValue placeholder={t("users.filters.role.label")} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="ALL">{t("users.filters.role.all")}</SelectItem>
                                    {Object.entries(USER_ROLE_NAMES).map(([key, value]) => (
                                        <SelectItem key={key} value={key}>
                                            {t(value)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>

                            <Select value={statusFilter} onValueChange={handleStatusChange}>
                                <SelectTrigger className="w-[180px]">
                                    <SelectValue placeholder={t("users.filters.status.label")} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="ALL">{t("users.filters.status.all")}</SelectItem>
                                    {Object.entries(USER_STATUS_NAMES).map(([key, value]) => (
                                        <SelectItem key={key} value={key}>
                                            {t(value)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </>
                    }
                />

                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="flex flex-1 items-center space-x-2">
                            <DataTableViewOptions table={table} />
                        </div>
                    </div>

                    <div className="rounded-md border">
                        <Table>
                            <TableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <TableHead key={header.id}>
                                                {header.isPlaceholder ? null : (
                                                    <div
                                                        className={
                                                            header.column.getCanSort()
                                                                ? "cursor-pointer select-none flex items-center space-x-2"
                                                                : "flex items-center space-x-2"
                                                        }
                                                        onClick={header.column.getToggleSortingHandler()}
                                                    >
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                        {{
                                                            asc: <ChevronsUp className="w-4 h-4" />,
                                                            desc: <ChevronsDown className="w-4 h-4" />,
                                                        }[header.column.getIsSorted() as string] ?? null}
                                                    </div>
                                                )}
                                            </TableHead>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableHeader>
                            <TableBody>
                                {users.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} className="h-24">
                                            <div className="flex items-center justify-center h-full flex-col gap-2">
                                                <CameraOff className="w-8 h-8 text-muted-foreground" />
                                                <span className="text-muted-foreground">{t("users.noData.description")}</span>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    table.getRowModel().rows.map((row) => (
                                        <TableRow
                                            key={row.id}
                                            className="hover:bg-muted/50"
                                        >
                                            {row.getVisibleCells().map((cell) => (
                                                <TableCell key={cell.id}>
                                                    {flexRender(
                                                        cell.column.columnDef.cell,
                                                        cell.getContext()
                                                    )}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    <DataTablePagination
                        table={table}
                        totalElements={totalElements}
                        totalPages={response?.totalPages || 0}
                    />
                </div>
            </div>
        </div>
    );
};

export default UsersPage;
